 

import { useState } from "react"
import "../styles/EducationalContent.css"

function EducationalContent({ activeSection, setActiveSection }) {
  const [expanded, setExpanded] = useState(true)

  const toggleExpand = () => {
    setExpanded(!expanded)
  }

  const sections = [
    { id: "overview", label: "Overview" },
    { id: "logic", label: "Step-by-Step Logic" },
    { id: "applications", label: "Applications" },
    { id: "complexity", label: "Time Complexity" },
  ]

  const renderSectionContent = () => {
    switch (activeSection) {
      case "overview":
        return (
          <div className="section-content">
            <h3>Boyer-Moore Algorithm</h3>
            <p>
              The Boyer-Moore string search algorithm is an efficient string searching algorithm that skips sections of
              the text to be searched, resulting in a lower constant factor than many other string algorithms.
            </p>
            <p>It uses two main heuristics:</p>
            <ul>
              <li>
                <strong>Bad Character Rule:</strong> If the character in the text doesn't match and is not in the
                pattern, we can shift the pattern past this character.
              </li>
              <li>
                <strong>Good Suffix Rule:</strong> If a mismatch occurs, we shift the pattern so that the matching
                suffix aligns with its next occurrence in the pattern.
              </li>
            </ul>
            <p>
              The algorithm preprocesses the pattern to create lookup tables for these rules, then uses them to
              determine how far to shift the pattern when a mismatch occurs.
            </p>
          </div>
        )
      case "logic":
        return (
          <div className="section-content">
            <h3>Step-by-Step Logic</h3>
            <ol>
              <li>
                <strong>Preprocessing:</strong> Create bad character and good suffix tables from the pattern.
                <ul>
                  <li>The bad character table stores the rightmost occurrence of each character in the pattern.</li>
                  <li>
                    The good suffix table stores information about the pattern's suffixes to enable larger shifts.
                  </li>
                </ul>
              </li>
              <li>
                <strong>Alignment:</strong> Align the pattern at the beginning of the text.
              </li>
              <li>
                <strong>Comparison:</strong> Compare characters from right to left.
                <ul>
                  <li>
                    This is a key insight of Boyer-Moore - by starting from the end, we can identify mismatches earlier.
                  </li>
                </ul>
              </li>
              <li>
                <strong>On Mismatch:</strong> Calculate shifts using both rules and take the maximum.
                <ul>
                  <li>
                    <strong>Bad Character Rule:</strong> Shift the pattern to align the mismatched character with its
                    rightmost occurrence in the pattern.
                  </li>
                  <li>
                    <strong>Good Suffix Rule:</strong> Shift the pattern to align the matched suffix with its previous
                    occurrence in the pattern.
                  </li>
                </ul>
              </li>
              <li>
                <strong>Shift:</strong> Move the pattern by the calculated shift amount.
              </li>
              <li>
                <strong>Repeat:</strong> Continue until the pattern reaches the end of the text.
              </li>
            </ol>
            <p>
              The key insight is that by starting comparisons from the end of the pattern, we can often skip large
              portions of the text, making the algorithm sublinear in many cases.
            </p>
          </div>
        )
      case "applications":
        return (
          <div className="section-content">
            <h3>Applications</h3>
            <ul>
              <li>
                <strong>Text editors:</strong> For "find" functionality, where Boyer-Moore's efficiency makes searches
                fast even in large documents.
              </li>
              <li>
                <strong>DNA sequence matching:</strong> In bioinformatics, where patterns need to be found in very long
                genomic sequences.
              </li>
              <li>
                <strong>Intrusion detection systems:</strong> For scanning network packets for specific patterns that
                might indicate attacks.
              </li>
              <li>
                <strong>Spam filters:</strong> To quickly identify keywords or patterns in emails that suggest spam
                content.
              </li>
              <li>
                <strong>Data compression algorithms:</strong> For identifying repeated patterns that can be compressed.
              </li>
              <li>
                <strong>Plagiarism detection software:</strong> To find matching text segments between documents.
              </li>
            </ul>
            <p>
              Boyer-Moore is particularly effective when the pattern is long and the alphabet is large, making it ideal
              for natural language processing and genomic data analysis.
            </p>
          </div>
        )
      case "complexity":
        return (
          <div className="section-content">
            <h3>Time Complexity</h3>
            <p>
              <strong>Preprocessing:</strong> O(m + σ) time and space, where m is the pattern length and σ is the
              alphabet size.
            </p>
            <p>
              <strong>Searching:</strong>
            </p>
            <ul>
              <li>
                <strong>Worst case:</strong> O(n×m) where n is the text length
                <ul>
                  <li>This occurs in pathological cases where many comparisons are needed at each position.</li>
                </ul>
              </li>
              <li>
                <strong>Best case:</strong> O(n/m)
                <ul>
                  <li>This happens when the pattern can be shifted by its full length after each comparison.</li>
                </ul>
              </li>
              <li>
                <strong>Average case:</strong> O(n)
                <ul>
                  <li>In practice, the algorithm often performs much better than the worst case.</li>
                </ul>
              </li>
            </ul>
            <p>
              Boyer-Moore performs better than naive algorithms (O(n×m)) and can even outperform linear-time algorithms
              like KMP in practice due to its ability to skip characters.
            </p>
            <p>
              The algorithm becomes more efficient as the pattern length increases and when there are fewer repeating
              characters in the pattern.
            </p>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div className={`educational-content ${expanded ? "expanded" : "collapsed"}`}>
      <div className="content-header" onClick={toggleExpand}>
        <h2>About the Algorithm</h2>
        <span className="expand-icon">{expanded ? "▼" : "▶"}</span>
      </div>

      {expanded && (
        <>
          <div className="section-tabs">
            {sections.map((section) => (
              <button
                key={section.id}
                className={`section-tab ${activeSection === section.id ? "active" : ""}`}
                onClick={() => setActiveSection(section.id)}
              >
                {section.label}
              </button>
            ))}
          </div>

          {renderSectionContent()}
        </>
      )}
    </div>
  )
}

export default EducationalContent
